import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../config/axios';
import { formatPickupWindow, formatDateForInput, formatTimeForInput, formatDateTime } from '../utils/dateTimeUtils';

interface Product {
  id: string;
  name_en: string;
  name_zh: string;
  description_en: string;
  description_zh: string;
  price_per_kg: number;
  is_active: boolean;
}

interface PickupLocation {
  id: string;
  name: string;
  address: string;
  is_active: boolean;
  created_at: string;
}

interface PickupInventory {
  id: string;
  product_id: string;
  product_name_en: string;
  product_name_zh: string;
  initial_quantity_kg: number;
  reserved_quantity_kg: number;
  available_quantity_kg: number;
}

interface PickupDate {
  id: string;
  location_id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  last_order_datetime: string | null;
  location: PickupLocation;
  is_active: boolean;
  inventories: PickupInventory[];
}

interface InventoryInput {
  product_id: string;
  initial_quantity_kg: number;
}

interface PickupDateInput {
  location_id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  last_order_datetime?: string;
  inventories: InventoryInput[];
}

const PickupManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const language = localStorage.getItem('language') || 'en';
  const [showForm, setShowForm] = useState(false);
  const [editingPickup, setEditingPickup] = useState<PickupDate | null>(null);
  const [formData, setFormData] = useState<PickupDateInput>({
    location_id: '',
    pickup_date: '',
    pickup_start_time: '',
    pickup_end_time: '',
    last_order_datetime: '',
    inventories: [],
  });
  const [error, setError] = useState<string>('');

  // Fetch all pickup dates
  const { data: pickupDates, isLoading: loadingPickups } = useQuery<PickupDate[]>({
    queryKey: ['adminPickupDates'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/admin/pickup-dates');
      return response.data;
    },
  });

  // Fetch all locations
  const { data: locations, isLoading: loadingLocations } = useQuery<PickupLocation[]>({
    queryKey: ['adminPickupLocations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/admin/pickup-locations');
      return response.data;
    },
  });

  // Fetch all active products
  const { data: products, isLoading: loadingProducts } = useQuery<Product[]>({
    queryKey: ['activeProducts'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/products');
      return response.data;
    },
  });

  // Create pickup date mutation
  const createMutation = useMutation({
    mutationFn: async (data: PickupDateInput) => {
      const response = await axiosInstance.post('/api/admin/pickup-dates', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPickupDates'] });
      resetForm();
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create pickup date');
    },
  });

  // Update pickup date mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PickupDateInput }) => {
      const response = await axiosInstance.put(`/api/admin/pickup-dates/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPickupDates'] });
      resetForm();
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update pickup date');
    },
  });

  // Delete pickup date mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/api/admin/pickup-dates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPickupDates'] });
    },
    onError: (err: any) => {
      alert(err.response?.data?.detail || 'Failed to delete pickup date');
    },
  });

  const resetForm = () => {
    setFormData({
      location_id: '',
      pickup_date: '',
      pickup_start_time: '',
      pickup_end_time: '',
      last_order_datetime: '',
      inventories: [],
    });
    setEditingPickup(null);
    setShowForm(false);
  };

  const handleEdit = (pickup: PickupDate) => {
    setEditingPickup(pickup);
    setFormData({
      location_id: pickup.location_id,
      pickup_date: formatDateForInput(pickup.pickup_date),
      pickup_start_time: formatTimeForInput(pickup.pickup_start_time),
      pickup_end_time: formatTimeForInput(pickup.pickup_end_time),
      last_order_datetime: pickup.last_order_datetime ? new Date(pickup.last_order_datetime).toISOString().slice(0, 16) : '',
      inventories: pickup.inventories.map(inv => ({
        product_id: inv.product_id,
        initial_quantity_kg: inv.initial_quantity_kg,
      })),
    });
    setShowForm(true);
    setError('');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this pickup date? This can only be done if there are no orders.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.location_id) {
      setError('Please select a location');
      return;
    }

    // Validate pickup date is in the future
    const pickupDate = new Date(formData.pickup_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (pickupDate < today) {
      setError('Pickup date must be in the future');
      return;
    }

    // Validate time window
    if (formData.pickup_end_time <= formData.pickup_start_time) {
      setError('Pickup end time must be after start time');
      return;
    }

    // Validate inventories
    if (formData.inventories.length === 0) {
      setError('Please add inventory for at least one product');
      return;
    }

    for (const inv of formData.inventories) {
      if (inv.initial_quantity_kg < 0) {
        setError('Inventory quantities must be non-negative');
        return;
      }
    }

    // Prepare data for submission
    const submitData: PickupDateInput = {
      location_id: formData.location_id,
      pickup_date: formData.pickup_date,
      pickup_start_time: formData.pickup_start_time,
      pickup_end_time: formData.pickup_end_time,
      inventories: formData.inventories,
    };

    // Only include last_order_datetime if it's set
    if (formData.last_order_datetime) {
      submitData.last_order_datetime = new Date(formData.last_order_datetime).toISOString();
    }

    if (editingPickup) {
      updateMutation.mutate({ id: editingPickup.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleInventoryChange = (productId: string, quantity: number) => {
    setFormData(prev => {
      const existingIndex = prev.inventories.findIndex(inv => inv.product_id === productId);

      if (existingIndex >= 0) {
        // Update existing inventory
        const newInventories = [...prev.inventories];
        newInventories[existingIndex] = { product_id: productId, initial_quantity_kg: quantity };
        return { ...prev, inventories: newInventories };
      } else {
        // Add new inventory
        return {
          ...prev,
          inventories: [...prev.inventories, { product_id: productId, initial_quantity_kg: quantity }],
        };
      }
    });
  };

  const getInventoryQuantity = (productId: string): number => {
    const inv = formData.inventories.find(i => i.product_id === productId);
    return inv?.initial_quantity_kg || 0;
  };

  if (loadingPickups || loadingLocations || loadingProducts) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Pickup Date Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              + Add Pickup Date
            </button>
          )}
          <a
            href="/admin"
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
            }}
          >
            ← Back to Dashboard
          </a>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <section style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #28a745' }}>
          <h2>{editingPickup ? 'Edit Pickup Date' : 'Add New Pickup Date'}</h2>

          {error && (
            <div style={{ padding: '10px', marginBottom: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Location Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Pickup Location *
              </label>
              <select
                value={formData.location_id}
                onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              >
                <option value="">Select a location...</option>
                {locations?.filter(loc => loc.is_active).map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name} - {location.address}
                  </option>
                ))}
              </select>
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                Don't see your location? <a href="/admin/locations" style={{ color: '#007bff' }}>Manage locations</a>
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              {/* Pickup Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Pickup Date *
                </label>
                <input
                  type="date"
                  value={formData.pickup_date}
                  onChange={(e) => setFormData({ ...formData, pickup_date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>

              {/* Pickup Start Time */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Start Time *
                </label>
                <input
                  type="time"
                  value={formData.pickup_start_time}
                  onChange={(e) => setFormData({ ...formData, pickup_start_time: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>

              {/* Pickup End Time */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  End Time *
                </label>
                <input
                  type="time"
                  value={formData.pickup_end_time}
                  onChange={(e) => setFormData({ ...formData, pickup_end_time: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>
            </div>

            {/* Last Order Datetime */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Last Order Deadline (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.last_order_datetime}
                onChange={(e) => setFormData({ ...formData, last_order_datetime: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              />
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                Orders cannot be placed after this time
              </p>
            </div>

            {/* Inventory Limits */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0 }}>Product Inventory Limits (KG)</h3>
              <p style={{ margin: '5px 0 15px 0', fontSize: '14px', color: '#666' }}>
                Set the available stock (in kilograms) for each active product for this pickup date.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', border: '1px solid #ddd' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 'bold' }}>
                        Product Name
                      </th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 'bold' }}>
                        Price per KG
                      </th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 'bold', width: '200px' }}>
                        Available Stock (KG)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {products?.map(product => (
                      <tr key={product.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          {language === 'zh' ? product.name_zh : product.name_en}
                        </td>
                        <td style={{ padding: '12px', color: '#666' }}>
                          €{product.price_per_kg.toFixed(2)}/kg
                        </td>
                        <td style={{ padding: '12px' }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={getInventoryQuantity(product.id) || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty string, numbers, and decimal point
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                const numValue = value === '' ? 0 : parseFloat(value);
                                handleInventoryChange(product.id, numValue);
                              }
                            }}
                            placeholder="0"
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              fontSize: '14px',
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingPickup ? 'Update' : 'Create')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Pickup Dates List */}
      <section>
        <h2>Scheduled Pickup Dates</h2>

        {pickupDates && pickupDates.length > 0 ? (
          <div style={{ display: 'grid', gap: '20px' }}>
            {pickupDates.map(pickup => (
              <div
                key={pickup.id}
                style={{
                  padding: '20px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                }}
              >
                {/* Pickup Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
                  <div>
                    <h3 style={{ margin: '0 0 5px 0' }}>{pickup.location.name}</h3>
                    <p style={{ margin: '5px 0', fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                      {formatPickupWindow(pickup.pickup_date, pickup.pickup_start_time, pickup.pickup_end_time, 'from', 'to')}
                    </p>
                    {pickup.last_order_datetime && (
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#dc3545', fontWeight: 'bold' }}>
                        Orders must be placed by {formatDateTime(pickup.last_order_datetime)}
                      </p>
                    )}
                    <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                      {pickup.location.address}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleEdit(pickup)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(pickup.id)}
                      disabled={deleteMutation.isPending}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Inventory Details */}
                <div>
                  <h4 style={{ margin: '0 0 10px 0' }}>Product Inventory</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {pickup.inventories.map(inv => {
                      const available = inv.initial_quantity_kg - inv.reserved_quantity_kg;
                      const percentageUsed = (inv.reserved_quantity_kg / inv.initial_quantity_kg) * 100;

                      return (
                        <div
                          key={inv.id}
                          style={{
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                          }}
                        >
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                            {language === 'zh' ? inv.product_name_zh : inv.product_name_en}
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Initial:</strong> {inv.initial_quantity_kg} kg
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Reserved:</strong> {inv.reserved_quantity_kg} kg
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '14px', color: available > 0 ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                            <strong>Available:</strong> {available.toFixed(1)} kg
                          </p>

                          {/* Progress Bar */}
                          <div style={{ marginTop: '10px', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                            <div
                              style={{
                                height: '100%',
                                width: `${percentageUsed}%`,
                                backgroundColor: percentageUsed >= 100 ? '#dc3545' : percentageUsed >= 75 ? '#ffc107' : '#28a745',
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
              No pickup dates scheduled yet.
            </p>
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '12px 30px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Add First Pickup Date
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default PickupManagementPage;
